import React from 'react';
import { Link } from 'react-router-dom';
import {
  IconSeed, IconTrowel, IconShears, IconBasket, IconEye,
} from './Icons';

// Original gardening guidance written for this app. RHS and Premier
// Seeds Direct are linked as further-reading references only - no text
// or images are copied from either site.

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// UK-specific monthly gardening tasks, broken down by category so the
// "what to grow/do this month" panel is genuinely useful at a glance
// rather than a single flat list. Selected automatically based on the
// visitor's current month.
const MONTH_GUIDE = {
  0: { // January
    sow: ['Chillies and aubergines on a warm windowsill', 'Broad beans under cover for an early crop'],
    plant: ['Bare-root fruit trees and roses while dormant', 'Bare-root hedging'],
    maintain: ['Prune apple and pear trees on a dry day', 'Force rhubarb under a pot for an early harvest', 'Check stored dahlia tubers for rot'],
    harvest: ['Leeks, parsnips and winter brassicas'],
    watch: ['Slug damage on any overwintering greens', 'Wind rock on newly planted trees - firm back in after gales'],
  },
  1: { // February
    sow: ['Early peas and broad beans under cover', 'Sweet peas for early flowers', 'Chillies and tomatoes indoors'],
    plant: ['Bare-root roses and fruit trees (last call before they break dormancy)', 'Snowdrops "in the green"'],
    maintain: ['Start dahlia tubers into growth in trays under glass', 'Prune winter-flowering shrubs once flowering finishes', 'Cut back ornamental grasses before new growth starts'],
    harvest: ['Purple sprouting broccoli', 'Forced rhubarb'],
    watch: ['Frost still a risk - keep fleece handy for early sowings'],
  },
  2: { // March
    sow: ['Hardy annuals and salad leaves outdoors', 'Carrots, beetroot and parsnips direct', 'Tomatoes and peppers under cover'],
    plant: ['Summer bulbs such as gladioli', 'Second early and maincrop potatoes'],
    maintain: ['Divide overgrown perennials', 'Chit and plant early potatoes', 'Mow the lawn on a dry day if growth has started'],
    harvest: ['Spring cabbage', 'Forced rhubarb'],
    watch: ['Slugs and snails becoming active as it warms up'],
  },
  3: { // April
    sow: ['Tender annuals under cover for planting out after frost', 'Courgettes, squash and beans undercover', 'Sweetcorn indoors'],
    plant: ['Dahlia tubers (mild areas, once frost risk has passed)', 'Summer bedding once hardened off'],
    maintain: ['Mulch beds to lock in spring moisture', 'Stake tall perennials before they flop', 'Feed roses as new growth appears'],
    harvest: ['Asparagus (established beds)', 'Spring greens'],
    watch: ['Late frosts - protect tender new growth overnight'],
  },
  4: { // May
    sow: ['Beans, sweetcorn and squash directly outdoors', 'Successional salads and beetroot'],
    plant: ['Dahlias, courgettes and tender vegetables after the last frost', 'Summer hanging baskets and containers'],
    maintain: ['Earth up potatoes', 'Take softwood cuttings from shrubs', 'Start watering new plantings regularly as the weather warms'],
    harvest: ['Asparagus', 'Early salads and radishes'],
    watch: ['Aphids on new soft growth - check the undersides of leaves'],
  },
  5: { // June
    sow: ['Quick-growing salads and beetroot for a summer harvest', 'Late sowings of carrots and beans'],
    plant: ['Out any remaining tender vegetables and bedding'],
    maintain: ['Deadhead roses regularly to encourage repeat flowering', 'Water new plantings well while they establish', 'Feed containers and hanging baskets weekly'],
    harvest: ['Strawberries', 'Broad beans', 'New potatoes'],
    watch: ['Dry spells starting - prioritise watering containers and new plants'],
  },
  6: { // July
    sow: ['Biennials like wallflowers and foxgloves for next spring', 'Spring cabbage for an autumn/winter crop'],
    plant: ['Autumn-fruiting raspberry canes', 'Late-season bedding to fill gaps'],
    maintain: ['Take semi-ripe cuttings from rosemary, lavender and other shrubs', 'Water deeply and mulch to conserve moisture in dry spells', 'Harvest and dry herbs before they flower'],
    harvest: ['Courgettes, tomatoes and cucumbers', 'Summer-fruiting raspberries and currants'],
    watch: ['Powdery mildew on courgettes/cucumbers in dry, humid weather', 'Water stress - check containers twice daily in heatwaves'],
  },
  7: { // August
    sow: ['Spring cabbages and autumn salads', 'Winter lettuce under cover'],
    plant: ['Autumn crocus and colchicum bulbs'],
    maintain: ['Take semi-ripe cuttings from figs, roses and rosemary', 'Support heavy dahlia and tomato stems', 'Water thoroughly during dry weather rather than little and often'],
    harvest: ['Sweetcorn', 'Runner and French beans', 'Plums and early apples'],
    watch: ['Blossom end rot on tomatoes from irregular watering'],
  },
  8: { // September
    sow: ['Green manure on empty vegetable beds', 'Winter salad leaves under cover'],
    plant: ['Spring bulbs such as daffodils and crocus', 'Spring cabbage plants'],
    maintain: ['Divide overcrowded perennials', 'Lift and store maincrop potatoes', 'Start reducing feed to roses to harden growth for winter'],
    harvest: ['Apples, pears and late plums', 'Maincrop potatoes', 'Pumpkins and squash'],
    watch: ['Early autumn frosts in northern/inland gardens - watch forecasts for tender plants'],
  },
  9: { // October
    sow: ['Broad beans for an overwintering crop'],
    plant: ['Tulip bulbs', 'Bare-root hedging and trees as they become available'],
    maintain: ['Lift dahlia tubers after the first frost blackens the foliage and store frost-free', 'Take hardwood cuttings from roses and fruit bushes', 'Rake up and compost fallen leaves'],
    harvest: ['Remaining apples and pears', 'Pumpkins before the first hard frost'],
    watch: ['First frosts - lift tender tubers and bring in tender container plants'],
  },
  10: { // November
    sow: ['Broad beans and garlic (still time in mild areas)'],
    plant: ['Bare-root roses and fruit trees', 'Tulip bulbs (can go in right through November)'],
    maintain: ['Protect tender pots from frost with fleece or bubble wrap', 'Insulate outdoor containers against freezing', 'Continue hardwood cuttings from currants and roses'],
    harvest: ['Leeks and winter brassicas', 'Remaining root vegetables before hard frost'],
    watch: ['Waterlogging in heavy soil - improve drainage on empty beds'],
  },
  11: { // December
    sow: ['Little outdoors - focus on planning next year\u2019s crops'],
    plant: ['Bare-root trees, shrubs and hedging on frost-free days'],
    maintain: ['Prune apple and pear trees on a dry, frost-free day', 'Check stored dahlia tubers for rot', 'Order seeds for the coming season', 'Mulch beds with well-rotted compost or manure'],
    harvest: ['Leeks, parsnips and Brussels sprouts (can be sweetened by frost)'],
    watch: ['Heavy snow settling on shrubs and hedges - knock off to prevent branch damage'],
  },
};

const SECTIONS = [
  { key: 'sow', label: 'Sow', Icon: IconSeed, tone: 't-green' },
  { key: 'plant', label: 'Plant', Icon: IconTrowel, tone: 't-rose' },
  { key: 'maintain', label: 'Maintain', Icon: IconShears, tone: 't-clay' },
  { key: 'harvest', label: 'Harvest', Icon: IconBasket, tone: 't-amber' },
  { key: 'watch', label: 'Watch for', Icon: IconEye, tone: 't-teal' },
];

/**
 * "What to do this month" - the seasonal panel that sits beside the
 * forecast on the home page. Anchored as #this-month so the stats strip
 * can link to it.
 */
export function MonthPanel() {
  const monthIndex = new Date().getMonth();
  const monthName = MONTH_NAMES[monthIndex];
  const guide = MONTH_GUIDE[monthIndex];

  return (
    <section id="this-month" aria-labelledby="month-heading">
      <div className="section-head">
        <div>
          <p className="eyebrow">In the garden</p>
          <h2 className="section-title" id="month-heading">What to do in {monthName}</h2>
        </div>
        <p className="section-note">South of England timings</p>
      </div>

      <div className="card month-panel">
        <p className="month-panel__intro">
          Timings assume the milder South of England climate - typically frost-free
          from mid-April to mid-October, so sowing and planting can usually start a
          couple of weeks earlier than in the Midlands or the north.
        </p>

        <div className="month-grid">
          {SECTIONS.map(({ key, label, Icon, tone }) => (
            <div className={`month-col ${tone}`} key={key}>
              <div className="month-col__head">
                <Icon />
                <h3>{label}</h3>
              </div>
              <ul>
                {guide[key].map(item => <li key={item}>{item}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Evergreen reference notes - watering, compost, propagation and
 * pointers into the full written guides.
 */
export function GuideNotes() {
  return (
    <section className="section" aria-labelledby="notes-heading">
      <div className="section-head">
        <div>
          <p className="eyebrow">Reference</p>
          <h2 className="section-title" id="notes-heading">Notes from the garden</h2>
        </div>
        <Link to="/guides" className="link-arrow">All growing guides</Link>
      </div>

      <div className="notes-grid">
        <article className="note-card note-card--wide t-plum">
          <h3 className="note-card__title">Propagating your garden</h3>
          <ul className="note-card__list">
            <li><strong>Cuttings</strong> (roses, rosemary, lavender, figs, currants, hydrangeas, fuchsias): take a healthy non-flowering shoot, remove the lower leaves, and insert into gritty, free-draining compost, keeping it out of direct sun and consistently moist until it roots.</li>
            <li><strong>Division</strong> (perennials like hostas, daylilies, geraniums): lift congested clumps in spring or autumn and split into sections, replanting straight away.</li>
            <li><strong>Layering</strong> (climbers like clematis and honeysuckle): bend a low stem to the ground, nick the bark, and pin it into soil until it roots on its own before separating it from the parent plant.</li>
            <li><strong>Grafting</strong> (fruit trees like apples and pears): joins a named fruiting variety onto a separate rootstock that controls the eventual tree size - most fruit trees are propagated this way rather than from cuttings.</li>
            <li><strong>Timing</strong>: softwood cuttings in late spring, semi-ripe in mid to late summer once stems are firm at the base, and hardwood in autumn and winter after growth has stopped.</li>
            <li>Keep soft cuttings humid under a clear bag or propagator lid so they do not wilt before they root, and pot on once a gentle tug meets resistance.</li>
          </ul>
          <p className="note-card__ref">
            Detailed how-to: <a href="https://www.rhs.org.uk/propagation" target="_blank" rel="noopener noreferrer">RHS propagation guides</a> (cuttings, division, layering, grafting)
          </p>
        </article>

        <article className="note-card t-teal">
          <h3 className="note-card__title">Watering in drought conditions</h3>
          <ul className="note-card__list">
            <li>Water deeply and infrequently rather than a light sprinkle every day - this trains roots to grow deeper and search for moisture.</li>
            <li>Water in the early morning or evening to cut evaporation loss.</li>
            <li>Prioritise newly planted trees, shrubs and containers - they have the shallowest roots and dry out fastest.</li>
            <li>Mulch bare soil with compost, bark or grass cuttings to slow moisture loss.</li>
            <li>Use grey water (for example cooled washing-up water) on ornamental beds where local rules allow.</li>
            <li>Established trees and shrubs are usually more drought-tolerant than lawns - let grass go brown rather than rationing water away from vegetables and young plants.</li>
            <li>Containers and hanging baskets dry out fastest of anything in the garden. Check them twice a day in a heatwave, morning and evening.</li>
            <li>A soaker hose or drip line puts water at the roots and loses far less to evaporation than a sprinkler, which throws most of it into the air.</li>
            <li>Water the soil rather than the foliage. Wet leaves in warm, humid weather invite mildew, and little of it reaches the roots anyway.</li>
          </ul>
          <p className="note-card__ref">
            Further reading: <a href="https://www.rhs.org.uk/garden-jobs/watering-in-drought" target="_blank" rel="noopener noreferrer">RHS drought advice</a>
          </p>
        </article>

        <article className="note-card t-clay">
          <h3 className="note-card__title">Making your own compost</h3>
          <ul className="note-card__list">
            <li>Layer greens (grass clippings, veg peelings, tea bags) with browns (cardboard, dry leaves, twigs) roughly 1:1 by volume.</li>
            <li>Chop woody material first so it breaks down faster.</li>
            <li>Turn the heap every few weeks to add air and speed up decomposition.</li>
            <li>Keep it as damp as a wrung-out sponge - too wet and it turns slimy, too dry and it stalls.</li>
            <li>Avoid meat, dairy and diseased plant material, which attract pests or spread disease.</li>
            <li>A well-managed heap is ready in 6-12 months, and can be used as a mulch or dug into beds to improve soil structure.</li>
            <li>Site the heap in light shade on bare soil where you can, so worms and microbes move in from below rather than having to be introduced.</li>
            <li>Size helps: a heap around a cubic metre holds its heat far better than a small bin, and that heat is what kills weed seeds.</li>
            <li>Sour smell or a slimy texture means too little air and too few browns - mix in torn cardboard and turn the heap.</li>
            <li>Nothing happening at all usually means too dry or too woody - water it and add fresh green material to restart it.</li>
            <li>Sieve the finished compost if you want a fine tilth for seed sowing, and throw the coarse leftovers back into the next heap.</li>
          </ul>
        </article>

        <article className="note-card t-green">
          <h3 className="note-card__title">Pruning roses &amp; fruit trees</h3>
          <ul className="note-card__list">
            <li>Bush and shrub roses: main prune in late winter, usually February, as the buds swell but before strong growth starts.</li>
            <li>Climbers and ramblers are pruned after flowering instead - most flower on last year&rsquo;s wood, so a winter cut removes the coming season&rsquo;s blooms.</li>
            <li>Start with the three Ds - dead, damaged and diseased wood - cutting back until the pith inside the stem is white rather than brown.</li>
            <li>Cut about 5mm above an outward-facing bud so new growth heads away from the centre. An open middle means better airflow and less blackspot.</li>
            <li>Apples and pears: prune while dormant, November to early March, on a dry, frost-free day.</li>
            <li>Plums, cherries and other stone fruit: summer only, June to August. Winter wounds are the main way silver leaf disease gets in, and it can kill the tree.</li>
            <li>Take no more than about a quarter of the canopy in one session, or the tree answers with vigorous leafy growth instead of fruit.</li>
            <li>Feed roses with a rose fertiliser and mulch with well-rotted manure or compost once pruned, to fuel the new season&rsquo;s growth.</li>
            <li>Bin prunings and fallen leaves rather than composting them if blackspot or rust was about, so you are not seeding the same problem next spring.</li>
          </ul>
          <p className="note-card__ref">
            Full guides: <Link to="/guides/pruning-roses">Pruning roses</Link> &middot; <Link to="/guides/pruning-fruit-trees">Pruning fruit trees</Link>
          </p>
        </article>

        <article className="note-card t-rose">
          <h3 className="note-card__title">Spring bulbs &amp; garlic</h3>
          <ul className="note-card__list">
            <li>Daffodils and crocus go in early, from September, so they root well before the ground turns cold. Alliums and hyacinths: October.</li>
            <li>Tulips are the exception - wait until November, or even early December, once the soil has cooled. Planting early raises the risk of tulip fire.</li>
            <li>Plant any bulb about three times its own height deep, measured from the base, and two to three bulb-widths apart.</li>
            <li>Drainage matters more than rich soil, since bulbs rot in waterlogged ground over winter. Work grit into heavy clay, especially for tulips.</li>
            <li>Leave the foliage to die back on its own for about six weeks after flowering - that is when the bulb recharges itself for next year.</li>
            <li>Garlic needs a cold spell to form a bulb at all, so plant cloves mid-October to December, pointy end up, 2.5-5cm deep and 15cm apart.</li>
            <li>Lift from June to August, once the lower leaves yellow while the top ones are still green, then cure somewhere warm and airy for two to four weeks.</li>
            <li>Most spring bulbs want sun to light shade. Woodland bulbs such as snowdrops and bluebells are the exception and prefer dappled shade under deciduous trees.</li>
            <li>For a naturalised drift in grass, scatter bulbs by hand and plant them where they land rather than in rows - it mimics how they would spread on their own.</li>
          </ul>
          <p className="note-card__ref">
            Full guides: <Link to="/guides/spring-flowering-bulbs">Spring bulbs</Link> &middot; <Link to="/guides/growing-garlic">Growing garlic</Link>
          </p>
        </article>

        <article className="note-card t-amber">
          <h3 className="note-card__title">Starting plants from seed &amp; tubers</h3>
          <ul className="note-card__list">
            <li>Starting tender plants indoors buys weeks on an unpredictable spring, so dahlias, sweet peas and tomatoes are already well grown when they go out.</li>
            <li>Most seeds germinate best at 18-21&deg;C. A heat mat or heated propagator holds the compost steady, which makes germination faster and more even.</li>
            <li>Once seedlings are up, drop to 15-18&deg;C. Too warm and they stretch and weaken as they reach for light.</li>
            <li>A windowsill is rarely bright enough in February and March. An LED grow light on a timer, 12-16 hours a day and 15-30cm above the leaves, keeps growth compact.</li>
            <li>Raise the light as the plants grow so the gap stays the same rather than scorching the tops.</li>
            <li>Water from below where you can, standing trays in shallow water for 10-15 minutes, to leave fine roots and the compost surface undisturbed.</li>
            <li>Keep compost moist but never waterlogged - permanently wet compost brings on damping-off.</li>
            <li>Harden off over 7 to 14 days before planting out. Skipping this is the single most common way seedlings are lost.</li>
          </ul>
          <p className="note-card__ref">
            Full guide: <Link to="/guides/starting-seeds-tubers-indoors">Starting seeds and tubers indoors</Link>
          </p>
        </article>
      </div>
    </section>
  );
}

export default function GardenGuide() {
  return (
    <>
      <MonthPanel />
      <GuideNotes />
    </>
  );
}
